/** Pure helpers of the 3D scatter (spec §9.7 `eisenhower3`), kept apart from the components. */

export interface Point {
  id: string;
  title: string;
  /** 0..1 on each axis */
  p: [number, number, number];
  color: string;
  key: string;
}

const SERIES = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5", "--chart-6"];
/** A colour as CSS: a token name (`--chart-1`) becomes `var(--chart-1)`, a hex value stays as it is. */
export const cssColour = (c: string) => (c.startsWith("--") ? `var(${c})` : c);

export function css(name: string): string {
  if (!name.startsWith("--")) return name; // already a colour value (axis colours)
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888888";
}

/** Colour follows the entity in a fixed order (first six groups by id), never its rank; the rest fold to "other". */
export function colorKeys(keys: string[]): Map<string, string> {
  const order = [...new Set(keys)].filter((k) => k !== "").sort();
  const out = new Map<string, string>();
  order.forEach((k, i) => out.set(k, i < SERIES.length ? SERIES[i] : "--chart-other"));
  // one series (nothing grouped) wears the accent; "other" only means "beyond the sixth group"
  out.set("", order.length === 0 ? "--chart-seq" : "--chart-other");
  return out;
}

/** The 2.5D projection used without WebGL (spec S4 fallback): rotate about y, tilt about x. */
export function project(p: [number, number, number], yaw: number, pitch: number): [number, number, number] {
  const [x, y, z] = p.map((v) => v - 0.5) as [number, number, number];
  const x1 = x * Math.cos(yaw) - z * Math.sin(yaw);
  const z1 = x * Math.sin(yaw) + z * Math.cos(yaw);
  const y1 = y * Math.cos(pitch) - z1 * Math.sin(pitch);
  const z2 = y * Math.sin(pitch) + z1 * Math.cos(pitch);
  return [x1, y1, z2];
}

export function hasWebGL(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}
